import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useAuth } from '../auth/AuthProvider'
import { supabase } from '../lib/supabase'
import {
  DEFAULT_VISUAL_TEMPLATE,
  VISUAL_TEMPLATE_SETTING_KEY,
  normalizeVisualTemplate,
  type VisualTemplate,
} from '../lib/visualTemplate'

type VisualTemplateContextValue = {
  globalTemplate: VisualTemplate
  effectiveTemplate: VisualTemplate
  previewTemplate: VisualTemplate | null
  loading: boolean
  saving: boolean
  remoteAvailable: boolean | null
  error: string
  preview: (template: VisualTemplate) => void
  clearPreview: () => void
  publish: (template: VisualTemplate) => Promise<boolean>
  refresh: () => Promise<boolean>
}

const VisualTemplateContext = createContext<VisualTemplateContextValue | null>(null)

export function VisualTemplateProvider({ children }: { children: ReactNode }) {
  const { session, profile } = useAuth()
  const [globalTemplate, setGlobalTemplate] = useState<VisualTemplate>(DEFAULT_VISUAL_TEMPLATE)
  const [previewTemplate, setPreviewTemplate] = useState<VisualTemplate | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [remoteAvailable, setRemoteAvailable] = useState<boolean | null>(null)
  const [error, setError] = useState('')

  const effectiveTemplate = previewTemplate ?? globalTemplate

  const refresh = useCallback(async () => {
    if (!session || !profile?.ativo) {
      setGlobalTemplate(DEFAULT_VISUAL_TEMPLATE)
      setRemoteAvailable(null)
      return false
    }

    const { data, error: queryError } = await supabase
      .from('app_settings')
      .select('value')
      .eq('key', VISUAL_TEMPLATE_SETTING_KEY)
      .maybeSingle()

    if (queryError) {
      setGlobalTemplate(DEFAULT_VISUAL_TEMPLATE)
      setRemoteAvailable(false)
      setError('Configuração global de aparência ainda não está instalada neste ambiente.')
      return false
    }

    setGlobalTemplate(normalizeVisualTemplate(data?.value))
    setRemoteAvailable(true)
    setError('')
    return true
  }, [profile?.ativo, session])

  useEffect(() => {
    let cancelled = false
    let intervalId: number | undefined

    if (!session || !profile?.ativo) {
      setGlobalTemplate(DEFAULT_VISUAL_TEMPLATE)
      setPreviewTemplate(null)
      setRemoteAvailable(null)
      setError('')
      return
    }

    setLoading(true)
    refresh()
      .then((available) => {
        if (cancelled || !available) return
        intervalId = window.setInterval(() => { void refresh() }, 60_000)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void refresh()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      cancelled = true
      if (intervalId) window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [profile?.ativo, refresh, session])

  useEffect(() => {
    document.documentElement.dataset.suiteTemplate = effectiveTemplate
    return () => {
      document.documentElement.dataset.suiteTemplate = DEFAULT_VISUAL_TEMPLATE
    }
  }, [effectiveTemplate])

  const publish = useCallback(async (template: VisualTemplate) => {
    if (profile?.role !== 'administrador' || !session) {
      setError('Somente Administrador pode publicar o template global.')
      return false
    }

    setSaving(true)
    setError('')
    const { error: updateError } = await supabase
      .from('app_settings')
      .upsert({
        key: VISUAL_TEMPLATE_SETTING_KEY,
        value: template,
        updated_by: session.user.id,
      }, { onConflict: 'key' })

    if (updateError) {
      setRemoteAvailable(false)
      setError('Não foi possível publicar o template global. A configuração do banco precisa estar instalada e acessível.')
      setSaving(false)
      return false
    }

    setGlobalTemplate(template)
    setPreviewTemplate(null)
    setRemoteAvailable(true)
    setSaving(false)
    return true
  }, [profile?.role, session])

  const value = useMemo<VisualTemplateContextValue>(() => ({
    globalTemplate,
    effectiveTemplate,
    previewTemplate,
    loading,
    saving,
    remoteAvailable,
    error,
    preview: setPreviewTemplate,
    clearPreview: () => setPreviewTemplate(null),
    publish,
    refresh,
  }), [effectiveTemplate, error, globalTemplate, loading, previewTemplate, publish, refresh, remoteAvailable, saving])

  return <VisualTemplateContext.Provider value={value}>{children}</VisualTemplateContext.Provider>
}

export function useVisualTemplate() {
  const context = useContext(VisualTemplateContext)
  if (!context) throw new Error('useVisualTemplate deve ser usado dentro de VisualTemplateProvider.')
  return context
}
